import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './RoleManagement.module.css';
import { roleApi, Role, RoleCreate, RoleUpdate, RoleUser } from '../../services/roleApi';

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showUsersModal, setShowUsersModal] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [viewingRole, setViewingRole] = useState<Role | null>(null);
  const [roleUsers, setRoleUsers] = useState<RoleUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [formData, setFormData] = useState<{ name: string; description: string }>({
    name: '',
    description: ''
  });
  const [saving, setSaving] = useState<boolean>(false);

  // Fetch roles on component mount
  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await roleApi.getAllRoles();
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to load roles. Please try again later.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setFormData({ name: role.name, description: role.description });
    setShowModal(true);
  };

  const handleViewUsers = async (role: Role) => {
    if (role.user_count === 0) {
      toast.info(`No users are assigned to the role "${role.name}"`, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setLoadingUsers(true);
      setViewingRole(role);
      setShowUsersModal(true);
      
      const users = await roleApi.getRoleUsers(role.Roles_ID);
      setRoleUsers(users);
    } catch (error) {
      console.error('Error fetching role users:', error);
      toast.error('Failed to load users for this role. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      setShowUsersModal(false);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCloseUsersModal = () => {
    setShowUsersModal(false);
    setViewingRole(null);
    setRoleUsers([]);
  };

  const handleDeleteRole = (role: Role) => {
    // Use toast.warn with custom confirmation UI
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete the role "{role.name}"?</p>
        {role.user_count > 0 && (
          <p className="mb-3 text-warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            This role is assigned to {role.user_count} user(s). Please remove role assignments first.
          </p>
        )}
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            disabled={role.user_count > 0}
            onClick={async () => {
              toast.dismiss();
              if (role.user_count > 0) return;
              
              try {
                await roleApi.deleteRole(role.Roles_ID);
                setRoles(prevRoles => prevRoles.filter(r => r.Roles_ID !== role.Roles_ID));
                toast.success(`Role "${role.name}" deleted successfully`, {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              } catch (error) {
                console.error('Error deleting role:', error);
                toast.error('Failed to delete role. Please try again.', {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: styles.deleteConfirmationToast,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Role name is required', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    try {
      setSaving(true);
      
      if (editingRole) {
        // Update existing role
        const updatedRole = await roleApi.updateRole(editingRole.Roles_ID, {
          name: formData.name.trim(),
          description: formData.description.trim()
        });
        
        setRoles(prevRoles => 
          prevRoles.map(role => 
            role.Roles_ID === editingRole.Roles_ID ? updatedRole : role
          )
        );
        
        toast.success(`Role "${updatedRole.name}" updated successfully`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } else {
        // Create new role
        const newRole = await roleApi.createRole({
          name: formData.name.trim(),
          description: formData.description.trim()
        });
        
        setRoles(prevRoles => [...prevRoles, newRole]);
        
        toast.success(`Role "${newRole.name}" created successfully`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
      
      setShowModal(false);
      setFormData({ name: '', description: '' });
      setEditingRole(null);
      
    } catch (error) {
      console.error('Error saving role:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Failed to save role: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ name: '', description: '' });
    setEditingRole(null);
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin">
                <i className="bi bi-house-door me-1"></i>Admin
              </Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Role Management
            </li>
          </ol>
        </nav>
        
        <div className={`${styles.mainBox} ${styles.clearfix}`}>
          <div className="table-responsive">
            <table className={`table ${styles.roleTable}`}>
              <tbody>
                <tr>
                  <td colSpan={4} className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2">Loading roles...</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/admin">
              <i className="bi bi-house-door me-1"></i>Admin
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Role Management
          </li>
        </ol>
      </nav>

      {/* Header with Create Button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div></div>
        <button 
          className="btn btn-primary"
          onClick={handleCreateRole}
        >
          Create new Role
        </button>
      </div>

      {/* Roles Table */}
      <div className={styles.roleListContainer}>
        <div className={`${styles.mainBox} ${styles.clearfix}`}>
          <div className="table-responsive">
            <table className={`table ${styles.roleTable}`}>
              <thead>
                <tr>
                  <th className={styles.roleTableHeader}>Name</th>
                  <th className={styles.roleTableHeader}>Description</th>
                  <th className={styles.roleTableHeader}>Users</th>
                  <th className={styles.roleTableHeader}>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {roles.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center">
                      No roles found. Create your first role to get started.
                    </td>
                  </tr>
                ) : (
                  roles.map((role) => (
                    <tr key={role.Roles_ID}>
                      <td className={styles.roleTableCell}>
                        <div className={styles.roleName}>{role.name}</div>
                      </td>
                      <td className={styles.roleTableCell}>
                        <div className={styles.roleDescription}>
                          {role.description || <em className="text-muted">No description</em>}
                        </div>
                      </td>
                      <td className={styles.roleTableCell}>
                        <span className={`badge ${role.user_count > 0 ? 'bg-info' : 'bg-secondary'}`}>
                          {role.user_count} user{role.user_count !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className={`text-end ${styles.roleTableCell}`}>
                        <div className={`btn-group ${styles.actionButtonsContainer}`}>
                          <button
                            className={`btn btn-sm btn-outline-primary ${styles.actionButton}`}
                            onClick={() => handleEditRole(role)}
                            title="Edit role"
                          >
                            <i className="bi bi-pencil-fill"></i>
                          </button>
                          <button
                            className={`btn btn-sm btn-outline-danger ${styles.actionButton}`}
                            onClick={() => handleDeleteRole(role)}
                            disabled={role.user_count > 0}
                            title={role.user_count > 0 ? "Cannot delete role with assigned users" : "Delete role"}
                          >
                            <i className="bi bi-trash-fill"></i>
                          </button>
                          <button
                            className={`btn btn-sm btn-outline-info ${styles.actionButton}`}
                            onClick={() => handleViewUsers(role)}
                            title="View users"
                          >
                            <i className="bi bi-eye-fill"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal for Create / Edit */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className={`modal-content ${styles.modalContent}`}>
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`bi ${editingRole ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
                  {editingRole ? 'Edit Role' : 'Create New Role'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  aria-label="Close"
                ></button>
              </div>
              <form onSubmit={handleSaveRole}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="roleName" className="form-label">
                      <i className="bi bi-tag me-1"></i>
                      Role Name *
                    </label>
                    <input
                      type="text"
                      id="roleName"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter role name"
                      required
                      maxLength={255}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="roleDescription" className="form-label">
                      <i className="bi bi-card-text me-1"></i>
                      Description
                    </label>
                    <textarea
                      id="roleDescription"
                      className="form-control"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Enter role description (optional)"
                      rows={4}
                      maxLength={500}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                    disabled={saving}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={saving || !formData.name.trim()}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className={`bi ${editingRole ? 'bi-check-circle' : 'bi-plus-circle'} me-1`}></i>
                        {editingRole ? 'Update Role' : 'Create Role'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Viewing Users in a Role */}
      {showUsersModal && viewingRole && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className={`modal-content ${styles.modalContent}`}>
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-people-fill me-2"></i>
                  Users in Role: {viewingRole.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseUsersModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {loadingUsers ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2">Loading users...</div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th><i className="bi bi-person me-1"></i>Name</th>
                          <th><i className="bi bi-envelope me-1"></i>Email</th>
                          <th><i className="bi bi-briefcase me-1"></i>Job Title</th>
                          <th><i className="bi bi-building me-1"></i>Firm</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roleUsers.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="text-center text-muted py-4">
                              <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                              <div>No users found for this role.</div>
                            </td>
                          </tr>
                        ) : (
                          roleUsers.map((user) => (
                            <tr key={user.User_ID}>
                              <td>
                                <strong>
                                  {user.title ? `${user.title} ` : ''}{user.firstname} {user.lastname}
                                </strong>
                              </td>
                              <td>
                                <a href={`mailto:${user.mail}`} className="text-decoration-none">
                                  {user.mail}
                                </a>
                              </td>
                              <td>
                                {user.jobtitle || <em className="text-muted">No job title</em>}
                              </td>
                              <td>
                                {user.firm ? (
                                  <span className="badge bg-light text-dark">
                                    {user.firm.name || user.firm.abbrev}
                                  </span>
                                ) : (
                                  <em className="text-muted">No firm</em>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseUsersModal}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 9999 }}
        limit={3}
      />
    </div>
  );
};

export default RoleManagement;
